import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { BRANDS } from "@/i18n/translations";
import SmartImg from "@/components/SmartImg";

function LangToggle({ className = "", light = false }) {
  const { lang, setLanguage } = useLang();
  const inactive = light ? "text-cream/70 hover:text-cream" : "text-inkmute hover:text-ink";
  return (
    <div className={`flex items-center gap-1 text-sm font-semibold ${className}`} data-testid="lang-toggle">
      <button
        data-testid="lang-id"
        onClick={() => setLanguage("id")}
        className={`px-2 py-1 transition-colors ${lang === "id" ? "text-claris" : inactive}`}
      >
        ID
      </button>
      <span className={light ? "text-cream/40" : "text-line"}>|</span>
      <button
        data-testid="lang-en"
        onClick={() => setLanguage("en")}
        className={`px-2 py-1 transition-colors ${lang === "en" ? "text-claris" : inactive}`}
      >
        EN
      </button>
    </div>
  );
}

export default function Navbar() {
  const { t, loc } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  const darkHero = location.pathname === "/" || location.pathname.startsWith("/brands");
  const light = !scrolled && darkHero; // light text over dark hero image
  const linkColor = light ? "text-cream hover:text-clara-accent" : "text-ink hover:text-claris";

  const navLinks = [
    { to: "/about", label: t("nav.about"), testid: "nav-about" },
    { to: "/certifications", label: t("nav.certs"), testid: "nav-certs" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-line" : "bg-transparent"
      }`}
      onMouseLeave={() => setMegaOpen(false)}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <SmartImg src="/logos/tpt.png" alt="PT Tan Putra Tama" priority className="h-11 w-auto object-contain shrink-0" />
          <span className="flex flex-col leading-none">
            <span className={`font-serif text-2xl md:text-[26px] tracking-tight ${light ? "text-cream" : "text-ink"}`}>Tan Putra Tama</span>
            <span className="text-[10px] uppercase tracking-[0.32em] text-claris font-semibold mt-0.5">The Baking Company</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          <button
            data-testid="nav-brands"
            onMouseEnter={() => setMegaOpen(true)}
            onClick={() => setMegaOpen(true)}
            aria-expanded={megaOpen}
            className={`text-sm font-semibold uppercase tracking-wider transition-colors ${megaOpen ? "text-claris" : linkColor}`}
          >
            {t("nav.brands")}
          </button>
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              onMouseEnter={() => setMegaOpen(false)}
              className={`text-sm font-semibold uppercase tracking-wider transition-colors ${linkColor}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <LangToggle light={light} />
          <Link
            to="/contact"
            data-testid="nav-quote-btn"
            className={`group inline-flex items-center gap-2 rounded-full pl-6 pr-5 py-3 text-sm font-semibold transition-colors duration-300 ${light ? "bg-cream text-ink hover:bg-claris hover:text-cream" : "bg-ink text-cream hover:bg-claris"}`}
          >
            {t("nav.requestQuote")}
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        {/* Mobile toggles */}
        <div className="flex lg:hidden items-center gap-3">
          <LangToggle light={light} />
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen((v) => !v)} className={`p-2 ${light ? "text-cream" : "text-ink"}`}>
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mega menu */}
      <AnimatePresence>
        {megaOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="hidden lg:block absolute top-20 left-0 w-full bg-cream border-b border-line shadow-[0_24px_60px_-20px_rgba(0,0,0,0.15)]"
            onMouseEnter={() => setMegaOpen(true)}
          >
            <div className="max-w-[1440px] mx-auto px-12 py-10 grid grid-cols-5 gap-5">
              {BRANDS.map((b) => (
                <Link
                  key={b.slug}
                  to={`/brands/${b.slug}`}
                  data-testid={`mega-brand-${b.slug}`}
                  className="group block border border-line hover:border-ink bg-white p-6 transition-colors duration-300"
                >
                  {b.logo ? (
                    <div className="h-12 mb-4 flex items-center">
                      <SmartImg src={b.logo} alt={b.name} className="max-h-12 max-w-[130px] object-contain" />
                    </div>
                  ) : (
                    <div className="h-1 w-10 mb-5 transition-all duration-300 group-hover:w-16" style={{ background: b.accent }} />
                  )}
                  <div className="font-serif text-2xl text-ink mb-1">{b.name}</div>
                  <div className="text-[11px] uppercase tracking-widest text-inkmute mb-1">{loc(b.tagline)}</div>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink group-hover:text-claris transition-colors">
                    {t("common.viewBrand")} <ArrowUpRight size={13} />
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden bg-cream border-b border-line overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              <div className="text-[11px] uppercase tracking-widest text-inkmute mb-2">{t("nav.brands")}</div>
              {BRANDS.map((b) => (
                <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`mobile-brand-${b.slug}`} className="flex items-center gap-3 py-2.5 text-ink">
                  <span className="h-2 w-2 rounded-full" style={{ background: b.accent }} />
                  <span className="font-serif text-xl">{b.name}</span>
                  <span className="text-xs text-inkmute">{loc(b.tagline)}</span>
                </Link>
              ))}
              <div className="h-px bg-line my-4" />
              {navLinks.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} className="py-2.5 text-sm font-semibold uppercase tracking-wider text-ink">
                  {l.label}
                </Link>
              ))}
              <Link to="/contact" className="mt-4 inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full py-3.5 text-sm font-semibold">
                {t("nav.requestQuote")} <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
